import { FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { PaymentMethod } from "@simplyk/common";
import { ExpressCheckoutElement as StripeExpressCheckoutElement } from "@stripe/react-stripe-js";
import { enqueueSnackbar } from "notistack";

import { PayAnotherWayDivider } from "./PayAnotherWayDivider";
import { useExpressCheckout } from "./useExpressCheckout";

import { useTranslate } from "@/common/hooks/useTranslate";
import { StripeExpressCheckoutApplePayPreview } from "@/forms-design-system/FormExpressCheckoutPaymentMethod/StripeExpressCheckoutApplePayPreview";
import { StripeExpressCheckoutGooglePayPreview } from "@/forms-design-system/FormExpressCheckoutPaymentMethod/StripeExpressCheckoutGooglePayPreview";
import { StripeExpressCheckoutSkeleton } from "@/forms-design-system/FormExpressCheckoutPaymentMethod/StripeExpressCheckoutSkeleton";
import { BaseFormPaymentMethod } from "@/forms-design-system/FormPaymentMethod/BaseFormPaymentMethod";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { CreditCard } from "@/icons/outlined";

export interface ExpressCheckoutButtonsProps {
  expressCheckout: ReturnType<typeof useExpressCheckout>;
  displayExpressCheckout: boolean;
  renderStripeButtons: boolean;
  renderPreviewButtons: boolean;
  belowPaymentMethods: boolean;
  isAuction: boolean;
  isOnboarding: boolean;
  selectedPaymentMethod: PaymentMethod | undefined;
  onSelectWallet: () => void;
}

export const ExpressCheckoutButtons: FC<ExpressCheckoutButtonsProps> = ({
  expressCheckout,
  displayExpressCheckout,
  renderStripeButtons,
  renderPreviewButtons,
  belowPaymentMethods,
  isAuction,
  isOnboarding,
  selectedPaymentMethod,
  onSelectWallet,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const isDarkMode = theme.palette.metadata?.mode === "dark";
  const {
    availableWallets,
    expressCheckoutKey,
    showExpressCheckoutLoader,
    handleExpressCheckoutReady,
    handleExpressCheckoutClick,
    handleExpressCheckoutConfirm,
    handleExpressCheckoutLoadError,
    handleExpressCheckoutCancel,
    handleExpressCheckoutShippingAddressChange,
    handleExpressCheckoutShippingRateChange,
  } = expressCheckout;

  const showOnboardingSnackbar = () => {
    enqueueSnackbar(t("common", "payment.expressCheckout.onboarding"), { vibe: "danger" });
  };

  const walletLabel = [availableWallets.applePay && "Apple Pay", availableWallets.googlePay && "Google Pay"]
    .filter(Boolean)
    .join(" / ");
  const isSelected = selectedPaymentMethod === PaymentMethod.ApplePayOrGooglePay;
  const showWalletRow = Boolean(walletLabel) && (renderPreviewButtons || displayExpressCheckout);
  const buttons = (
    <Stack gap={0.5}>
      {renderStripeButtons && !renderPreviewButtons && (
        <StripeExpressCheckoutElement
          key={expressCheckoutKey}
          id="express-checkout-element"
          onReady={handleExpressCheckoutReady}
          onLoadError={handleExpressCheckoutLoadError}
          onConfirm={handleExpressCheckoutConfirm}
          onCancel={handleExpressCheckoutCancel}
          onShippingAddressChange={handleExpressCheckoutShippingAddressChange}
          onShippingRateChange={handleExpressCheckoutShippingRateChange}
          onClick={isOnboarding ? showOnboardingSnackbar : handleExpressCheckoutClick}
          options={{
            buttonTheme: {
              applePay: isDarkMode ? "white" : "black",
              googlePay: isDarkMode ? "white" : "black",
            },
            ...(isAuction && { buttonType: { googlePay: "plain" } }),
          }}
        />
      )}
      {renderPreviewButtons && (
        <>
          {availableWallets.applePay && <StripeExpressCheckoutApplePayPreview />}
          {availableWallets.googlePay && <StripeExpressCheckoutGooglePayPreview />}
        </>
      )}
      {renderStripeButtons && !renderPreviewButtons && showExpressCheckoutLoader && !belowPaymentMethods && (
        <StripeExpressCheckoutSkeleton />
      )}
      {isAuction && (renderPreviewButtons || !showExpressCheckoutLoader) && (
        <FormTypography vibe="moderate" variant="caption" component="p" marginTop={1}>
          {t("common", "payment.expressCheckout.auctionHelper")}
        </FormTypography>
      )}
    </Stack>
  );

  if (!renderStripeButtons && !renderPreviewButtons) {
    return null;
  }

  if (belowPaymentMethods) {
    return (
      <Box sx={{ width: "100%", display: showWalletRow ? "block" : "none" }}>
        <BaseFormPaymentMethod
          data-test="payment-method-wallets"
          icon={<CreditCard />}
          label={walletLabel}
          lastItem
          checked={isSelected}
          onChange={onSelectWallet}
        >
          {buttons}
        </BaseFormPaymentMethod>
      </Box>
    );
  }

  return (
    <Stack sx={{ width: "100%", display: displayExpressCheckout || renderPreviewButtons ? "flex" : "none" }} mb={3}>
      {buttons}
      {(renderPreviewButtons || !showExpressCheckoutLoader) && <PayAnotherWayDivider sx={{ marginBlockStart: 3 }} />}
    </Stack>
  );
};
